import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';

import styles from './styles/styles.module.css';

import {
  createIncidentReport,
  deleteIncidentReportAttachment,
  deleteIncidentReportDraft,
  getIncidentReportById,
  getIncidentReportConfig,
  getIncidentReports,
  submitIncidentReport,
  updateIncidentReportDraft,
  uploadIncidentReportAttachments,
  feetInchesToCm,
  cmToFeetInches,
  type CreateIncidentReportPayload,
  type IncidentAttachment,
  type IncidentReport as IncidentReportRecord,
  type IncidentReportConfig,
  type IncidentReportSeverity,
  type IncidentReportStatus,
  type IncidentReportType,
  type IncidentPersonRole,
  type IncidentMedicalNature,
  type IncidentMedicalCareAction,
  type IncidentHazardType,
} from '../../../../api/incidentReports/post';

import type { IncidentReportSocketUpdatePayload } from '../../../../Redux/slices/incidentReportSlice';

import {
  BsActivity,
  BsBookmark,
  BsCarFrontFill,
  BsClockHistory,
  BsConeStriped,
  BsExclamationTriangle,
  BsGeoAltFill,
  BsPersonFill,
  BsSendFill,
  BsShieldFillExclamation,
  BsThreeDots,
} from 'react-icons/bs';

/* =========================================================
   TYPES
   ========================================================= */

type TriBool = boolean | '';

type HistoryRangePreset = '7' | '30' | '90' | '180' | '365' | 'CUSTOM';

type PersonForm = {
  name: string;
  role: IncidentPersonRole | '';
  roleOther: string;
  notes: string;
};

type IncidentForm = {
  reportType: IncidentReportType;

  title: string;
  occurredAt: string;
  locationLabel: string;

  severity: IncidentReportSeverity | '';

  personsInvolved: PersonForm[];

  narrative: string;
  actionsTaken: string;

  notifySupervisor: boolean;

  medical: {
    incidentNatures: IncidentMedicalNature[];

    incidentNatureOther: string;

    careActions: IncidentMedicalCareAction[];

    careActionOther: string;

    careProvidedNarrative: string;

    emsCalled: TriBool;

    emsAgency: string;

    transported: TriBool;

    destination: string;

    refusedCare: TriBool;

    additionalNotes: string;
  };

  securityIncident: {
    incidentSubtype: string;

    incidentSubtypeOther: string;

    lawEnforcementContacted: TriBool;

    agency: string;

    officerName: string;

    caseNumber: string;

    additionalNotes: string;
  };

  suspiciousPerson: {
    sexPresentation: string;

    approximateAgeRange: string;

    /*
     * UI only.
     *
     * These are converted to centimeters
     * before the request is sent.
     */
    heightFeet: string;

    heightInches: string;

    build: string;

    clothing: string;

    hairColor: string;

    hairLength: string;

    facialHair: string;

    identifyingFeatures: string;

    behavior: string;

    directionOfTravel: string;

    associatedVehicle: string;
  };

  suspiciousVehicle: {
    licensePlate: string;

    state: string;

    make: string;

    model: string;

    color: string;

    bodyType: string;

    occupantCount: string;

    distinguishingMarks: string;

    directionOfTravel: string;
  };

  fireSmoke: {
    sourceLocation: string;

    suspectedSource: string;

    suspectedSourceOther: string;

    alarmActivated: TriBool;

    extinguisherUsed: TriBool;

    evacuationOccurred: TriBool;

    fireDepartmentCalled: TriBool;

    fireDepartmentAgency: string;

    additionalNotes: string;
  };

  safetyHazard: {
    hazardTypes: IncidentHazardType[];

    hazardTypeOther: string;

    areaSecured: TriBool;

    correctiveAction: string;

    additionalNotes: string;
  };

  accessControl: {
    accessPointType: string;

    accessPointOther: string;

    accessMethod: string;

    accessMethodOther: string;

    authorized: TriBool;

    credentialDescription: string;

    attemptedEntry: TriBool;

    additionalNotes: string;
  };

  propertyDamage: {
    propertyCategory: string;

    propertyCategoryOther: string;

    propertyDescription: string;

    suspectedCause: string;

    suspectedCauseOther: string;

    estimatedDamageAmount: string;

    additionalNotes: string;
  };

  theftLoss: {
    lossType: string;

    propertyCategory: string;

    propertyCategoryOther: string;

    itemDescription: string;

    estimatedValue: string;

    lastKnownLocation: string;

    lastKnownAt: string;

    lawEnforcementContacted: TriBool;

    agency: string;

    caseNumber: string;

    additionalNotes: string;
  };

  clientDissatisfaction: {
    complaintSource: string;

    complaintSourceOther: string;

    complaintCategory: string;

    complaintCategoryOther: string;

    complaintSummary: string;

    employeeOrTeamInvolved: string;

    immediateResolution: string;

    followUpRequired: TriBool;

    additionalNotes: string;
  };

  clientPraise: {
    praiseSource: string;

    praiseSourceOther: string;

    praisedEmployeeUserId: string;

    praisedEmployeeNameSnapshot: string;

    praiseCategory: string;

    praiseCategoryOther: string;

    praiseSummary: string;

    additionalNotes: string;
  };
};

/* =========================================================
   REPORT TYPE META
   ========================================================= */

const REPORT_TYPE_META: Record<
  IncidentReportType,
  {
    label: string;
    icon: React.ReactNode;
  }
> = {
  MEDICAL: {
    label: 'Medical',

    icon: <BsActivity />,
  },

  SECURITY_INCIDENT: {
    label: 'Security Incident',

    icon: <BsShieldFillExclamation />,
  },

  SUSPICIOUS_PERSON: {
    label: 'Suspicious Person',

    icon: <BsPersonFill />,
  },

  SUSPICIOUS_VEHICLE: {
    label: 'Suspicious Vehicle',

    icon: <BsCarFrontFill />,
  },

  FIRE_SMOKE: {
    label: 'Fire / Smoke',

    icon: <BsExclamationTriangle />,
  },

  SAFETY_HAZARD: {
    label: 'Safety Hazard',

    icon: <BsConeStriped />,
  },

  ACCESS_CONTROL: {
    label: 'Access Control',

    icon: <BsShieldFillExclamation />,
  },

  PROPERTY_DAMAGE: {
    label: 'Property Damage',

    icon: <BsExclamationTriangle />,
  },

  THEFT_LOSS: {
    label: 'Theft / Loss',

    icon: <BsExclamationTriangle />,
  },

  CLIENT_DISSATISFACTION: {
    label: 'Client Dissatisfaction',

    icon: <BsPersonFill />,
  },

  CLIENT_PRAISE: {
    label: 'Client Praise',

    icon: <BsPersonFill />,
  },

  OTHER: {
    label: 'Other',

    icon: <BsThreeDots />,
  },
};

/* =========================================================
   ENUM DISPLAY LABELS
   ========================================================= */

const SPECIAL_LABELS: Record<string, string> = {
  AGE_18_24: '18–24',

  AGE_25_34: '25–34',

  AGE_35_44: '35–44',

  AGE_45_54: '45–54',

  AGE_55_64: '55–64',

  AGE_65_PLUS: '65+',

  VERY_SLIM: 'Very Slim',

  VERY_HEAVY: 'Very Heavy',

  VERY_SHORT: 'Very Short',

  LAW_ENFORCEMENT: 'Law Enforcement',

  FIRE_DEPARTMENT: 'Fire Department',

  FAMILY_MEMBER: 'Family Member',

  COMMERCIAL_TRUCK: 'Commercial Truck',

  BOX_TRUCK: 'Box Truck',

  CUT_LACERATION: 'Cut / Laceration',

  BLUNT_TRAUMA: 'Blunt Trauma',

  HEAD_INJURY: 'Head Injury',

  ALLERGIC_REACTION: 'Allergic Reaction',

  BREATHING_DIFFICULTY: 'Breathing Difficulty',

  CHEST_PAIN: 'Chest Pain',

  LOSS_OF_CONSCIOUSNESS: 'Loss of Consciousness',

  DIABETIC_EMERGENCY: 'Diabetic Emergency',

  HEAT_ILLNESS: 'Heat Illness',

  CARDIAC_ARREST: 'Cardiac Arrest',

  BASIC_FIRST_AID: 'Basic First Aid',

  BLEEDING_CONTROL: 'Bleeding Control',

  EMS_TRANSFER: 'EMS Transfer',

  MONITORED_ONLY: 'Monitored Only',

  UNAUTHORIZED_ENTRY: 'Unauthorized Entry',

  DOMESTIC_INCIDENT: 'Domestic Incident',

  MISSING_PERSON: 'Missing Person',

  POLICE_ACTIVITY: 'Police Activity',

  SECURITY_BREACH: 'Security Breach',

  POLICY_VIOLATION: 'Policy Violation',

  SLIP_TRIP_FALL: 'Slip / Trip / Fall',

  BLOCKED_EXIT: 'Blocked Exit',

  SHARP_OBJECT: 'Sharp Object',

  BROKEN_GLASS: 'Broken Glass',

  WATER_LEAK: 'Water Leak',

  VEHICLE_TRAFFIC: 'Vehicle / Traffic',

  POOL_WATER: 'Pool / Water',

  PIN_CODE: 'PIN Code',

  REMOTE_RELEASE: 'Remote Release',

  FORCED_ENTRY: 'Forced Entry',

  UNLOCKED_DOOR: 'Unlocked Door',

  UNLOCKED_GATE: 'Unlocked Gate',

  VEHICLE_GATE: 'Vehicle Gate',

  PEDESTRIAN_GATE: 'Pedestrian Gate',

  FRONT_DOOR: 'Front Door',

  REAR_DOOR: 'Rear Door',

  SERVICE_ENTRANCE: 'Service Entrance',

  RESIDENTIAL_AREA: 'Residential Area',

  DOOR_GATE: 'Door / Gate',

  WINDOW_GLASS: 'Window / Glass',

  SECURITY_EQUIPMENT: 'Security Equipment',

  ART_VALUABLE: 'Art / Valuable',

  PERSONAL_PROPERTY: 'Personal Property',

  MECHANICAL_FAILURE: 'Mechanical Failure',

  ACCESS_CREDENTIAL: 'Access Credential',

  ESTATE_MANAGER: 'Estate Manager',

  EXECUTIVE_ASSISTANT: 'Executive Assistant',

  HOUSEHOLD_STAFF: 'Household Staff',

  CLIENT_REPRESENTATIVE: 'Client Representative',

  SECURITY_PROCEDURE: 'Security Procedure',

  SECURITY_RESPONSE: 'Security Response',

  PROBLEM_SOLVING: 'Problem Solving',

  DYED_OTHER: 'Dyed / Other',

  MULTICOLOR: 'Multicolor',

  NORTHEAST: 'Northeast',

  SOUTHEAST: 'Southeast',

  SOUTHWEST: 'Southwest',

  NORTHWEST: 'Northwest',
};

function enumLabel(value: string) {
  if (SPECIAL_LABELS[value]) {
    return SPECIAL_LABELS[value];
  }

  return value
    .toLowerCase()
    .split('_')
    .map((section) => section.charAt(0).toUpperCase() + section.slice(1))
    .join(' ');
}

/* =========================================================
   HELPERS
   ========================================================= */

function localDateTimeValue(timezone: string) {
  return moment()
    .tz(timezone || 'America/Los_Angeles')
    .format('YYYY-MM-DDTHH:mm');
}

function localInputToIso(
  value: string,

  timezone: string
) {
  if (!value) {
    return undefined;
  }

  const parsed = moment.tz(
    value,

    'YYYY-MM-DDTHH:mm',

    timezone || 'America/Los_Angeles'
  );

  return parsed.isValid() ? parsed.toISOString() : undefined;
}

function isoToLocalInput(
  value: string | undefined,

  timezone: string
) {
  if (!value) {
    return '';
  }

  return moment(value)
    .tz(timezone || 'America/Los_Angeles')
    .format('YYYY-MM-DDTHH:mm');
}

function optionalString(value: string) {
  const normalized = value.trim();

  return normalized || undefined;
}

function optionalNumber(value: string) {
  if (!value.trim()) {
    return undefined;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : undefined;
}

function optionalInteger(value: string) {
  const parsed = optionalNumber(value);

  if (parsed === undefined) {
    return undefined;
  }

  return Number.isInteger(parsed) ? parsed : undefined;
}

function optionalBoolean(value: TriBool) {
  return value === '' ? undefined : value;
}

function toTriBool(value: boolean | undefined): TriBool {
  if (value === true) {
    return true;
  }

  if (value === false) {
    return false;
  }

  return '';
}

function getErrorMessage(
  error: any,

  fallback: string
) {
  return error?.response?.data?.msg || error?.message || fallback;
}

/* =========================================================
   ATTACHMENT HELPERS
   ========================================================= */

const MAX_ATTACHMENT_COUNT = 10;

const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;

const ALLOWED_ATTACHMENT_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'application/pdf',
]);

function formatFileSize(bytes: number | undefined) {
  if (bytes === undefined || !Number.isFinite(bytes)) {
    return '';
  }

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* =========================================================
   EMPTY FORM
   ========================================================= */

function createEmptyForm(
  timezone: string,

  reportType: IncidentReportType = 'MEDICAL'
): IncidentForm {
  return {
    reportType,

    title: '',

    occurredAt: localDateTimeValue(timezone),

    locationLabel: '',

    severity: '',

    personsInvolved: [],

    narrative: '',

    actionsTaken: '',

    notifySupervisor: true,

    medical: {
      incidentNatures: [],

      incidentNatureOther: '',

      careActions: [],

      careActionOther: '',

      careProvidedNarrative: '',

      emsCalled: '',

      emsAgency: '',

      transported: '',

      destination: '',

      refusedCare: '',

      additionalNotes: '',
    },

    securityIncident: {
      incidentSubtype: '',

      incidentSubtypeOther: '',

      lawEnforcementContacted: '',

      agency: '',

      officerName: '',

      caseNumber: '',

      additionalNotes: '',
    },

    suspiciousPerson: {
      sexPresentation: '',

      approximateAgeRange: '',

      heightFeet: '',

      heightInches: '',

      build: '',

      clothing: '',

      hairColor: '',

      hairLength: '',

      facialHair: '',

      identifyingFeatures: '',

      behavior: '',

      directionOfTravel: '',

      associatedVehicle: '',
    },

    suspiciousVehicle: {
      licensePlate: '',

      state: '',

      make: '',

      model: '',

      color: '',

      bodyType: '',

      occupantCount: '',

      distinguishingMarks: '',

      directionOfTravel: '',
    },

    fireSmoke: {
      sourceLocation: '',

      suspectedSource: '',

      suspectedSourceOther: '',

      alarmActivated: '',

      extinguisherUsed: '',

      evacuationOccurred: '',

      fireDepartmentCalled: '',

      fireDepartmentAgency: '',

      additionalNotes: '',
    },

    safetyHazard: {
      hazardTypes: [],

      hazardTypeOther: '',

      areaSecured: '',

      correctiveAction: '',

      additionalNotes: '',
    },

    accessControl: {
      accessPointType: '',

      accessPointOther: '',

      accessMethod: '',

      accessMethodOther: '',

      authorized: '',

      credentialDescription: '',

      attemptedEntry: '',

      additionalNotes: '',
    },

    propertyDamage: {
      propertyCategory: '',

      propertyCategoryOther: '',

      propertyDescription: '',

      suspectedCause: '',

      suspectedCauseOther: '',

      estimatedDamageAmount: '',

      additionalNotes: '',
    },

    theftLoss: {
      lossType: '',

      propertyCategory: '',

      propertyCategoryOther: '',

      itemDescription: '',

      estimatedValue: '',

      lastKnownLocation: '',

      lastKnownAt: '',

      lawEnforcementContacted: '',

      agency: '',

      caseNumber: '',

      additionalNotes: '',
    },

    clientDissatisfaction: {
      complaintSource: '',

      complaintSourceOther: '',

      complaintCategory: '',

      complaintCategoryOther: '',

      complaintSummary: '',

      employeeOrTeamInvolved: '',

      immediateResolution: '',

      followUpRequired: '',

      additionalNotes: '',
    },

    clientPraise: {
      praiseSource: '',

      praiseSourceOther: '',

      praisedEmployeeUserId: '',

      praisedEmployeeNameSnapshot: '',

      praiseCategory: '',

      praiseCategoryOther: '',

      praiseSummary: '',

      additionalNotes: '',
    },
  };
}

/* =========================================================
   REPORT -> FORM
   ========================================================= */

function reportToForm(
  report: IncidentReportRecord,

  timezone: string
): IncidentForm {
  const base = createEmptyForm(
    timezone,

    report.reportType
  );

  const height = cmToFeetInches(report.suspiciousPerson?.estimatedHeightCm);

  return {
    ...base,

    reportType: report.reportType,

    title: report.title ?? '',

    occurredAt: isoToLocalInput(
      report.occurredAt,

      timezone
    ),

    locationLabel: report.locationLabel ?? '',

    severity: report.severity ?? '',

    personsInvolved: (report.personsInvolved ?? []).map((person) => ({
      name: person.name ?? '',

      role: person.role ?? '',

      roleOther: person.roleOther ?? '',

      notes: person.notes ?? '',
    })),

    narrative: report.narrative ?? '',

    actionsTaken: report.actionsTaken ?? '',

    notifySupervisor: report.notifySupervisor ?? true,

    medical: {
      ...base.medical,

      incidentNatures: report.medical?.incidentNatures ?? [],

      incidentNatureOther: report.medical?.incidentNatureOther ?? '',

      careActions: report.medical?.careActions ?? [],

      careActionOther: report.medical?.careActionOther ?? '',

      careProvidedNarrative: report.medical?.careProvidedNarrative ?? '',

      emsCalled: toTriBool(report.medical?.emsCalled),

      emsAgency: report.medical?.emsAgency ?? '',

      transported: toTriBool(report.medical?.transported),

      destination: report.medical?.destination ?? '',

      refusedCare: toTriBool(report.medical?.refusedCare),

      additionalNotes: report.medical?.additionalNotes ?? '',
    },

    securityIncident: {
      ...base.securityIncident,

      ...(report.securityIncident ?? {}),

      lawEnforcementContacted: toTriBool(
        report.securityIncident?.lawEnforcementContacted
      ),
    },

    suspiciousPerson: {
      ...base.suspiciousPerson,

      ...(report.suspiciousPerson ?? {}),

      heightFeet: height ? String(height.feet) : '',

      heightInches: height ? String(height.inches) : '',
    },

    suspiciousVehicle: {
      ...base.suspiciousVehicle,

      ...(report.suspiciousVehicle ?? {}),

      occupantCount:
        report.suspiciousVehicle?.occupantCount !== undefined
          ? String(report.suspiciousVehicle.occupantCount)
          : '',
    },

    fireSmoke: {
      ...base.fireSmoke,

      ...(report.fireSmoke ?? {}),

      alarmActivated: toTriBool(report.fireSmoke?.alarmActivated),

      extinguisherUsed: toTriBool(report.fireSmoke?.extinguisherUsed),

      evacuationOccurred: toTriBool(report.fireSmoke?.evacuationOccurred),

      fireDepartmentCalled: toTriBool(report.fireSmoke?.fireDepartmentCalled),
    },

    safetyHazard: {
      ...base.safetyHazard,

      ...(report.safetyHazard ?? {}),

      areaSecured: toTriBool(report.safetyHazard?.areaSecured),
    },

    accessControl: {
      ...base.accessControl,

      ...(report.accessControl ?? {}),

      authorized: toTriBool(report.accessControl?.authorized),

      attemptedEntry: toTriBool(report.accessControl?.attemptedEntry),
    },

    propertyDamage: {
      ...base.propertyDamage,

      ...(report.propertyDamage ?? {}),

      estimatedDamageAmount:
        report.propertyDamage?.estimatedDamageAmount !== undefined
          ? String(report.propertyDamage.estimatedDamageAmount)
          : '',
    },

    theftLoss: {
      ...base.theftLoss,

      ...(report.theftLoss ?? {}),

      estimatedValue:
        report.theftLoss?.estimatedValue !== undefined
          ? String(report.theftLoss.estimatedValue)
          : '',

      lastKnownAt: isoToLocalInput(
        report.theftLoss?.lastKnownAt,

        timezone
      ),

      lawEnforcementContacted: toTriBool(
        report.theftLoss?.lawEnforcementContacted
      ),
    },

    clientDissatisfaction: {
      ...base.clientDissatisfaction,

      ...(report.clientDissatisfaction ?? {}),

      followUpRequired: toTriBool(
        report.clientDissatisfaction?.followUpRequired
      ),
    },

    clientPraise: {
      ...base.clientPraise,

      ...(report.clientPraise ?? {}),
    },
  };
}

/* =========================================================
   FORM -> API
   ========================================================= */

function buildReportFields(
  form: IncidentForm,

  timezone: string
): Omit<CreateIncidentReportPayload, 'siteId'> {
  const common: Omit<CreateIncidentReportPayload, 'siteId'> = {
    reportType: form.reportType,

    title: optionalString(form.title),

    occurredAt: localInputToIso(
      form.occurredAt,

      timezone
    ),

    locationLabel: optionalString(form.locationLabel),

    severity: form.severity || undefined,

    personsInvolved: form.personsInvolved
      .map((person) => ({
        name: optionalString(person.name),

        role: person.role || undefined,

        roleOther:
          person.role === 'OTHER'
            ? optionalString(person.roleOther)
            : undefined,

        notes: optionalString(person.notes),
      }))
      .filter((person) => Boolean(person.name || person.role || person.notes)),

    narrative: optionalString(form.narrative),

    actionsTaken: optionalString(form.actionsTaken),

    notifySupervisor: form.notifySupervisor,
  };

  switch (form.reportType) {
    case 'MEDICAL':
      return {
        ...common,

        medical: {
          incidentNatures: form.medical.incidentNatures,

          incidentNatureOther: form.medical.incidentNatures.includes('OTHER')
            ? optionalString(form.medical.incidentNatureOther)
            : undefined,

          careActions: form.medical.careActions,

          careActionOther: form.medical.careActions.includes('OTHER')
            ? optionalString(form.medical.careActionOther)
            : undefined,

          careProvidedNarrative: optionalString(
            form.medical.careProvidedNarrative
          ),

          emsCalled: optionalBoolean(form.medical.emsCalled),

          emsAgency: optionalString(form.medical.emsAgency),

          transported: optionalBoolean(form.medical.transported),

          destination: optionalString(form.medical.destination),

          refusedCare: optionalBoolean(form.medical.refusedCare),

          additionalNotes: optionalString(form.medical.additionalNotes),
        },
      };

    case 'SECURITY_INCIDENT':
      return {
        ...common,

        securityIncident: {
          incidentSubtype: form.securityIncident.incidentSubtype || undefined,

          incidentSubtypeOther:
            form.securityIncident.incidentSubtype === 'OTHER'
              ? optionalString(form.securityIncident.incidentSubtypeOther)
              : undefined,

          lawEnforcementContacted: optionalBoolean(
            form.securityIncident.lawEnforcementContacted
          ),

          agency: optionalString(form.securityIncident.agency),

          officerName: optionalString(form.securityIncident.officerName),

          caseNumber: optionalString(form.securityIncident.caseNumber),

          additionalNotes: optionalString(
            form.securityIncident.additionalNotes
          ),
        } as any,
      };

    case 'SUSPICIOUS_PERSON': {
      let estimatedHeightCm: number | undefined;

      if (form.suspiciousPerson.heightFeet) {
        estimatedHeightCm = feetInchesToCm(
          Number(form.suspiciousPerson.heightFeet),

          Number(form.suspiciousPerson.heightInches || 0)
        );
      }

      return {
        ...common,

        suspiciousPerson: {
          sexPresentation: form.suspiciousPerson.sexPresentation || undefined,

          approximateAgeRange:
            form.suspiciousPerson.approximateAgeRange || undefined,

          estimatedHeightCm,

          build: form.suspiciousPerson.build || undefined,

          clothing: optionalString(form.suspiciousPerson.clothing),

          hairColor: form.suspiciousPerson.hairColor || undefined,

          hairLength: form.suspiciousPerson.hairLength || undefined,

          facialHair: form.suspiciousPerson.facialHair || undefined,

          identifyingFeatures: optionalString(
            form.suspiciousPerson.identifyingFeatures
          ),

          behavior: optionalString(form.suspiciousPerson.behavior),

          directionOfTravel:
            form.suspiciousPerson.directionOfTravel || undefined,

          associatedVehicle: optionalString(
            form.suspiciousPerson.associatedVehicle
          ),
        } as any,
      };
    }

    case 'SUSPICIOUS_VEHICLE':
      return {
        ...common,

        suspiciousVehicle: {
          licensePlate: optionalString(form.suspiciousVehicle.licensePlate),

          state: form.suspiciousVehicle.state || undefined,

          make: optionalString(form.suspiciousVehicle.make),

          model: optionalString(form.suspiciousVehicle.model),

          color: form.suspiciousVehicle.color || undefined,

          bodyType: form.suspiciousVehicle.bodyType || undefined,

          occupantCount: optionalInteger(form.suspiciousVehicle.occupantCount),

          distinguishingMarks: optionalString(
            form.suspiciousVehicle.distinguishingMarks
          ),

          directionOfTravel:
            form.suspiciousVehicle.directionOfTravel || undefined,
        } as any,
      };

    case 'FIRE_SMOKE':
      return {
        ...common,

        fireSmoke: {
          sourceLocation: optionalString(form.fireSmoke.sourceLocation),

          suspectedSource: form.fireSmoke.suspectedSource || undefined,

          suspectedSourceOther:
            form.fireSmoke.suspectedSource === 'OTHER'
              ? optionalString(form.fireSmoke.suspectedSourceOther)
              : undefined,

          alarmActivated: optionalBoolean(form.fireSmoke.alarmActivated),

          extinguisherUsed: optionalBoolean(form.fireSmoke.extinguisherUsed),

          evacuationOccurred: optionalBoolean(
            form.fireSmoke.evacuationOccurred
          ),

          fireDepartmentCalled: optionalBoolean(
            form.fireSmoke.fireDepartmentCalled
          ),

          fireDepartmentAgency: optionalString(
            form.fireSmoke.fireDepartmentAgency
          ),

          additionalNotes: optionalString(form.fireSmoke.additionalNotes),
        } as any,
      };

    case 'SAFETY_HAZARD':
      return {
        ...common,

        safetyHazard: {
          hazardTypes: form.safetyHazard.hazardTypes,

          hazardTypeOther: form.safetyHazard.hazardTypes.includes('OTHER')
            ? optionalString(form.safetyHazard.hazardTypeOther)
            : undefined,

          areaSecured: optionalBoolean(form.safetyHazard.areaSecured),

          correctiveAction: optionalString(form.safetyHazard.correctiveAction),

          additionalNotes: optionalString(form.safetyHazard.additionalNotes),
        },
      };

    case 'ACCESS_CONTROL':
      return {
        ...common,

        accessControl: {
          accessPointType: form.accessControl.accessPointType || undefined,

          accessPointOther:
            form.accessControl.accessPointType === 'OTHER'
              ? optionalString(form.accessControl.accessPointOther)
              : undefined,

          accessMethod: form.accessControl.accessMethod || undefined,

          accessMethodOther:
            form.accessControl.accessMethod === 'OTHER'
              ? optionalString(form.accessControl.accessMethodOther)
              : undefined,

          authorized: optionalBoolean(form.accessControl.authorized),

          credentialDescription: optionalString(
            form.accessControl.credentialDescription
          ),

          attemptedEntry: optionalBoolean(form.accessControl.attemptedEntry),

          additionalNotes: optionalString(form.accessControl.additionalNotes),
        } as any,
      };

    case 'PROPERTY_DAMAGE':
      return {
        ...common,

        propertyDamage: {
          propertyCategory: form.propertyDamage.propertyCategory || undefined,

          propertyCategoryOther:
            form.propertyDamage.propertyCategory === 'OTHER'
              ? optionalString(form.propertyDamage.propertyCategoryOther)
              : undefined,

          propertyDescription: optionalString(
            form.propertyDamage.propertyDescription
          ),

          suspectedCause: form.propertyDamage.suspectedCause || undefined,

          suspectedCauseOther:
            form.propertyDamage.suspectedCause === 'OTHER'
              ? optionalString(form.propertyDamage.suspectedCauseOther)
              : undefined,

          estimatedDamageAmount: optionalNumber(
            form.propertyDamage.estimatedDamageAmount
          ),

          additionalNotes: optionalString(form.propertyDamage.additionalNotes),
        } as any,
      };

    case 'THEFT_LOSS':
      return {
        ...common,

        theftLoss: {
          lossType: form.theftLoss.lossType || undefined,

          propertyCategory: form.theftLoss.propertyCategory || undefined,

          propertyCategoryOther:
            form.theftLoss.propertyCategory === 'OTHER'
              ? optionalString(form.theftLoss.propertyCategoryOther)
              : undefined,

          itemDescription: optionalString(form.theftLoss.itemDescription),

          estimatedValue: optionalNumber(form.theftLoss.estimatedValue),

          lastKnownLocation: optionalString(form.theftLoss.lastKnownLocation),

          lastKnownAt: localInputToIso(
            form.theftLoss.lastKnownAt,

            timezone
          ),

          lawEnforcementContacted: optionalBoolean(
            form.theftLoss.lawEnforcementContacted
          ),

          agency: optionalString(form.theftLoss.agency),

          caseNumber: optionalString(form.theftLoss.caseNumber),

          additionalNotes: optionalString(form.theftLoss.additionalNotes),
        } as any,
      };

    case 'CLIENT_DISSATISFACTION':
      return {
        ...common,

        clientDissatisfaction: {
          complaintSource:
            form.clientDissatisfaction.complaintSource || undefined,

          complaintSourceOther:
            form.clientDissatisfaction.complaintSource === 'OTHER'
              ? optionalString(form.clientDissatisfaction.complaintSourceOther)
              : undefined,

          complaintCategory:
            form.clientDissatisfaction.complaintCategory || undefined,

          complaintCategoryOther:
            form.clientDissatisfaction.complaintCategory === 'OTHER'
              ? optionalString(
                  form.clientDissatisfaction.complaintCategoryOther
                )
              : undefined,

          complaintSummary: optionalString(
            form.clientDissatisfaction.complaintSummary
          ),

          employeeOrTeamInvolved: optionalString(
            form.clientDissatisfaction.employeeOrTeamInvolved
          ),

          immediateResolution: optionalString(
            form.clientDissatisfaction.immediateResolution
          ),

          followUpRequired: optionalBoolean(
            form.clientDissatisfaction.followUpRequired
          ),

          additionalNotes: optionalString(
            form.clientDissatisfaction.additionalNotes
          ),
        } as any,
      };

    case 'CLIENT_PRAISE':
      return {
        ...common,

        clientPraise: {
          praiseSource: form.clientPraise.praiseSource || undefined,

          praiseSourceOther:
            form.clientPraise.praiseSource === 'OTHER'
              ? optionalString(form.clientPraise.praiseSourceOther)
              : undefined,

          praisedEmployeeUserId: optionalString(
            form.clientPraise.praisedEmployeeUserId
          ),

          praisedEmployeeNameSnapshot: optionalString(
            form.clientPraise.praisedEmployeeNameSnapshot
          ),

          praiseCategory: form.clientPraise.praiseCategory || undefined,

          praiseCategoryOther:
            form.clientPraise.praiseCategory === 'OTHER'
              ? optionalString(form.clientPraise.praiseCategoryOther)
              : undefined,

          praiseSummary: optionalString(form.clientPraise.praiseSummary),

          additionalNotes: optionalString(form.clientPraise.additionalNotes),
        } as any,
      };

    default:
      return common;
  }
}

/* =========================================================
   COMPONENT HELPERS
   ========================================================= */

const Field: React.FC<{
  label: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ label, required, className, children }) => (
  <label className={[styles.field, className ?? ''].filter(Boolean).join(' ')}>
    <span>
      {label}

      {required && <b className={styles.required}>*</b>}
    </span>

    {children}
  </label>
);

const ControlledSelect: React.FC<{
  value: string;
  options: readonly string[];
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}> = ({ value, options, placeholder = 'Select', disabled, onChange }) => (
  <select
    value={value}
    disabled={disabled}
    onChange={(event) => onChange(event.target.value)}
  >
    <option value="">{placeholder}</option>

    {options.map((option) => (
      <option key={option} value={option}>
        {enumLabel(option)}
      </option>
    ))}
  </select>
);

const TriStateSelect: React.FC<{
  value: TriBool;
  disabled?: boolean;
  onChange: (value: TriBool) => void;
}> = ({ value, disabled, onChange }) => (
  <select
    value={value === '' ? '' : value ? 'YES' : 'NO'}
    disabled={disabled}
    onChange={(event) => {
      if (event.target.value === 'YES') {
        onChange(true);

        return;
      }

      if (event.target.value === 'NO') {
        onChange(false);

        return;
      }

      onChange('');
    }}
  >
    <option value="">Not recorded</option>

    <option value="YES">Yes</option>

    <option value="NO">No</option>
  </select>
);

const MultiSelect: React.FC<{
  options: readonly string[];
  values: readonly string[];
  disabled?: boolean;
  onChange: (values: string[]) => void;
}> = ({ options, values, disabled, onChange }) => (
  <div className={styles.multiSelectGrid}>
    {options.map((option) => {
      const selected = values.includes(option);

      return (
        <button
          key={option}
          type="button"
          disabled={disabled}
          className={[
            styles.multiSelectOption,

            selected ? styles.multiSelectOptionActive : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => {
            if (selected) {
              onChange(values.filter((item) => item !== option));

              return;
            }

            onChange([...values, option]);
          }}
        >
          <span className={styles.multiSelectCheck}>{selected ? '✓' : ''}</span>

          {enumLabel(option)}
        </button>
      );
    })}
  </div>
);

/* =========================================================
   MAIN
   ========================================================= */

const IncidentReport: React.FC = () => {
  const currentSite = useSelector((state: any) => state.currentSite ?? {});

  const siteId = currentSite?.siteId ?? '';

  const siteName = currentSite?.siteName ?? '';

  const timezone = currentSite?.timezone || 'America/Los_Angeles';

  const validSite = Boolean(
    siteId && siteId !== '0000' && siteName !== 'MASTER'
  );

  const incidentReportRealtimeVersion = useSelector(
    (state: any) => state.incidentReport?.versionBySite?.[siteId] ?? 0
  );

  const lastIncidentReportUpdate = useSelector(
    (state: any) => state.incidentReport?.lastUpdate ?? null
  ) as IncidentReportSocketUpdatePayload | null;

  /* =====================================================
       CONFIG
       ===================================================== */

  const [config, setConfig] = useState<IncidentReportConfig | null>(null);

  const [configLoading, setConfigLoading] = useState(true);

  const [configError, setConfigError] = useState('');

  const loadConfig = useCallback(async () => {
    setConfigLoading(true);

    setConfigError('');

    try {
      const response = await getIncidentReportConfig();

      if (!response.success || !response.data) {
        throw new Error(
          response.msg || 'Unable to load incident report configuration.'
        );
      }

      setConfig(response.data);
    } catch (error: any) {
      setConfig(null);

      setConfigError(
        getErrorMessage(
          error,

          'Unable to load standardized incident report values.'
        )
      );
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  /* =====================================================
       FORM
       ===================================================== */

  const [form, setForm] = useState<IncidentForm>(() =>
    createEmptyForm(timezone)
  );

  const [reportId, setReportId] = useState<string | null>(null);

  const [status, setStatus] = useState<IncidentReportStatus | null>(null);

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState('');

  const [success, setSuccess] = useState('');

  const [showHistory, setShowHistory] = useState(false);

  const [reports, setReports] = useState<IncidentReportRecord[]>([]);

  const [historyLoading, setHistoryLoading] = useState(false);

  const [attachments, setAttachments] = useState<IncidentAttachment[]>([]);

  const [uploadingAttachments, setUploadingAttachments] = useState(false);

  const [deletingAttachmentId, setDeletingAttachmentId] = useState<
    string | null
  >(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [deletingReportId, setDeletingReportId] = useState<string | null>(null);

  const [historyRange, setHistoryRange] = useState<HistoryRangePreset>('30');

  const [customFrom, setCustomFrom] = useState('');

  const [customTo, setCustomTo] = useState('');

  const readOnly = status === 'SUBMITTED';

  useEffect(() => {
    const defaultType = config?.reportTypes?.[0] ?? 'MEDICAL';

    setForm(
      createEmptyForm(
        timezone,

        defaultType
      )
    );

    setReportId(null);

    setStatus(null);

    setAttachments([]);

    setUploadingAttachments(false);

    setDeletingAttachmentId(null);

    setReports([]);

    setError('');

    setSuccess('');
  }, [siteId, timezone, config]);

  const setSection = (
    section: keyof IncidentForm,

    key: string,

    value: any
  ) => {
    setForm((previous) => ({
      ...previous,

      [section]: {
        ...(previous as any)[section],

        [key]: value,
      },
    }));
  };

  /* =====================================================
       HISTORY
       ===================================================== */

  const loadHistory = useCallback(async () => {
    if (!validSite) {
      setReports([]);

      return;
    }

    setHistoryLoading(true);

    try {
      let from: string | undefined;

      let to: string | undefined;

      if (historyRange === 'CUSTOM') {
        if (
          customFrom &&
          customTo &&
          moment(customFrom, 'YYYY-MM-DD').isAfter(
            moment(customTo, 'YYYY-MM-DD'),
            'day'
          )
        ) {
          setReports([]);

          setError('The history start date cannot be after the end date.');

          return;
        }

        if (customFrom) {
          from = moment
            .tz(customFrom, 'YYYY-MM-DD', timezone)
            .startOf('day')
            .toISOString();
        }

        if (customTo) {
          to = moment
            .tz(customTo, 'YYYY-MM-DD', timezone)
            .endOf('day')
            .toISOString();
        }
      } else {
        const days = Number(historyRange);

        from = moment()
          .tz(timezone)
          .subtract(days - 1, 'days')
          .startOf('day')
          .toISOString();

        to = moment().tz(timezone).endOf('day').toISOString();
      }

      setError('');

      const response = await getIncidentReports({
        siteId,

        from,

        to,

        limit: 250,

        skip: 0,
      });

      if (!response.success) {
        throw new Error(response.msg || 'Unable to load report history.');
      }

      setReports(response.data?.reports ?? []);
    } catch (error: any) {
      setError(
        getErrorMessage(
          error,

          'Unable to load report history.'
        )
      );
    } finally {
      setHistoryLoading(false);
    }
  }, [validSite, siteId, timezone, historyRange, customFrom, customTo]);

  useEffect(() => {
    if (showHistory) {
      void loadHistory();
    }
  }, [showHistory, loadHistory]);

  /* =====================================================
       PERSONS
       ===================================================== */

  const addPerson = () => {
    setForm((previous) => ({
      ...previous,

      personsInvolved: [
        ...previous.personsInvolved,

        {
          name: '',

          role: '',

          roleOther: '',

          notes: '',
        },
      ],
    }));
  };

  const updatePerson = (
    index: number,

    field: keyof PersonForm,

    value: string
  ) => {
    setForm((previous) => {
      const persons = [...previous.personsInvolved];

      persons[index] = {
        ...persons[index],

        [field]: value,
      };

      if (field === 'role' && value !== 'OTHER') {
        persons[index].roleOther = '';
      }

      return {
        ...previous,

        personsInvolved: persons,
      };
    });
  };

  /* =====================================================
       VALIDATE
       ===================================================== */

  const validate = () => {
    if (!form.title.trim()) {
      return 'Report title is required.';
    }

    if (!form.occurredAt) {
      return 'Incident date and time are required.';
    }

    if (!form.locationLabel.trim()) {
      return 'Incident location is required.';
    }

    if (!form.severity) {
      return 'Severity is required.';
    }

    if (!form.narrative.trim()) {
      return 'Narrative is required.';
    }

    if (!form.actionsTaken.trim()) {
      return 'Actions Taken is required.';
    }

    for (const person of form.personsInvolved) {
      if (person.role === 'OTHER' && !person.roleOther.trim()) {
        return 'Please specify the other person role.';
      }
    }

    if (form.reportType === 'MEDICAL') {
      if (
        form.medical.incidentNatures.includes('OTHER') &&
        !form.medical.incidentNatureOther.trim()
      ) {
        return 'Please specify the other medical incident type.';
      }

      if (
        form.medical.careActions.includes('OTHER') &&
        !form.medical.careActionOther.trim()
      ) {
        return 'Please specify the other medical care action.';
      }
    }

    if (
      form.reportType === 'SECURITY_INCIDENT' &&
      form.securityIncident.incidentSubtype === 'OTHER' &&
      !form.securityIncident.incidentSubtypeOther.trim()
    ) {
      return 'Please specify the other security incident subtype.';
    }

    if (
      form.reportType === 'FIRE_SMOKE' &&
      form.fireSmoke.suspectedSource === 'OTHER' &&
      !form.fireSmoke.suspectedSourceOther.trim()
    ) {
      return 'Please specify the other fire source.';
    }

    if (
      form.reportType === 'SAFETY_HAZARD' &&
      form.safetyHazard.hazardTypes.includes('OTHER') &&
      !form.safetyHazard.hazardTypeOther.trim()
    ) {
      return 'Please specify the other safety hazard.';
    }

    if (form.reportType === 'SUSPICIOUS_PERSON') {
      if (
        !form.suspiciousPerson.heightFeet &&
        form.suspiciousPerson.heightInches
      ) {
        return 'Select feet before inches.';
      }

      if (form.suspiciousPerson.heightFeet) {
        const cm = feetInchesToCm(
          Number(form.suspiciousPerson.heightFeet),

          Number(form.suspiciousPerson.heightInches || 0)
        );

        if (cm < 90 || cm > 250) {
          return 'Estimated height must be between 90 cm and 250 cm.';
        }
      }
    }

    return '';
  };

  /* =====================================================
       SAVE
       ===================================================== */

  const saveDraft = async () => {
    if (!validSite || readOnly) {
      return;
    }

    setSaving(true);

    setError('');

    setSuccess('');

    try {
      const fields = buildReportFields(
        form,

        timezone
      );

      const response = reportId
        ? await updateIncidentReportDraft(
            reportId,

            siteId,

            fields
          )
        : await createIncidentReport({
            siteId,

            ...fields,
          });

      if (!response.success || !response.data) {
        throw new Error(response.msg || 'Unable to save report.');
      }

      setReportId(response.data._id);

      setStatus(response.data.status);

      setForm(
        reportToForm(
          response.data,

          timezone
        )
      );

      setSuccess('Incident report draft saved.');

      if (showHistory) {
        await loadHistory();
      }
    } catch (error: any) {
      setError(
        getErrorMessage(
          error,

          'Unable to save incident report.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  /* =====================================================
       SUBMIT
       ===================================================== */

  const submit = async () => {
    const validation = validate();

    if (validation) {
      setError(validation);

      return;
    }

    if (
      !window.confirm(
        'Submit this incident report? Submitted reports cannot be edited.'
      )
    ) {
      return;
    }

    setSaving(true);

    setError('');

    try {
      const fields = buildReportFields(
        form,

        timezone
      );

      let id = reportId;

      if (id) {
        const saveResponse = await updateIncidentReportDraft(
          id,

          siteId,

          fields
        );

        if (!saveResponse.success) {
          throw new Error(
            saveResponse.msg || 'Unable to save report before submission.'
          );
        }
      } else {
        const createResponse = await createIncidentReport({
          siteId,

          ...fields,
        });

        if (!createResponse.success || !createResponse.data) {
          throw new Error(createResponse.msg || 'Unable to create report.');
        }

        id = createResponse.data._id;
      }

      if (!id) {
        throw new Error('Report ID was not returned.');
      }

      const response = await submitIncidentReport(
        id,

        siteId
      );

      if (!response.success || !response.data) {
        throw new Error(response.msg || 'Unable to submit report.');
      }

      setReportId(response.data._id);

      setStatus(response.data.status);

      setForm(
        reportToForm(
          response.data,

          timezone
        )
      );

      setSuccess('Incident report submitted successfully.');

      await loadHistory();
    } catch (error: any) {
      setError(
        getErrorMessage(
          error,

          'Unable to submit incident report.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  /* =====================================================
       OPEN REPORT
       ===================================================== */

  const openReport = async (id: string) => {
    try {
      const response = await getIncidentReportById(
        id,

        siteId
      );

      if (!response.success || !response.data) {
        throw new Error(response.msg || 'Unable to open report.');
      }

      setReportId(response.data._id);

      setStatus(response.data.status);

      setAttachments(response.data.attachments ?? []);

      setForm(
        reportToForm(
          response.data,

          timezone
        )
      );

      window.scrollTo({
        top: 0,

        behavior: 'smooth',
      });
    } catch (error: any) {
      setError(
        getErrorMessage(
          error,

          'Unable to open report.'
        )
      );
    }
  };

  /*
   * Refresh the currently-open report without scrolling
   * the user back to the top of the page.
   */
  const refreshOpenReportFromRealtime = useCallback(async () => {
    if (!reportId || !validSite) {
      return;
    }

    try {
      const response = await getIncidentReportById(
        reportId,

        siteId
      );

      if (!response.success || !response.data) {
        throw new Error(response.msg || 'Unable to refresh incident report.');
      }

      setStatus(response.data.status);

      setAttachments(response.data.attachments ?? []);

      setForm(
        reportToForm(
          response.data,

          timezone
        )
      );
    } catch (error: any) {
      setError(
        getErrorMessage(
          error,

          'Unable to refresh incident report.'
        )
      );
    }
  }, [reportId, validSite, siteId, timezone]);

  /*
   * Socket.IO only tells us that something changed.
   *
   * Refetch from the normal authorized HTTP endpoints so
   * Mongo/API stay authoritative.
   */
  useEffect(() => {
    if (
      !incidentReportRealtimeVersion ||
      !lastIncidentReportUpdate ||
      lastIncidentReportUpdate.siteId !== siteId
    ) {
      return;
    }

    if (showHistory) {
      void loadHistory();
    }

    if (!reportId || lastIncidentReportUpdate.reportId !== reportId) {
      return;
    }

    /*
     * Another device may delete a draft that is still
     * open here. Clear it locally instead of trying to
     * refetch a report that no longer exists.
     */
    if (lastIncidentReportUpdate.action === 'DELETED') {
      const defaultType = config?.reportTypes?.[0] ?? 'MEDICAL';

      setReportId(null);

      setStatus(null);

      setAttachments([]);

      setDeletingAttachmentId(null);

      setForm(
        createEmptyForm(
          timezone,

          defaultType
        )
      );

      setSuccess('This incident report draft was deleted on another device.');

      return;
    }

    void refreshOpenReportFromRealtime();
  }, [
    incidentReportRealtimeVersion,
    lastIncidentReportUpdate,
    siteId,
    showHistory,
    loadHistory,
    reportId,
    config,
    timezone,
    refreshOpenReportFromRealtime,
  ]);

  /* =====================================================
       DELETE DRAFT
       ===================================================== */

  const deleteDraft = async (report: IncidentReportRecord) => {
    if (report.status !== 'DRAFT') {
      return;
    }

    const confirmed = window.confirm(
      `Delete draft "${report.title || 'Untitled Report'}"? This cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    setDeletingReportId(report._id);

    setError('');

    setSuccess('');

    try {
      const response = await deleteIncidentReportDraft(
        report._id,

        siteId
      );

      if (!response.success) {
        throw new Error(response.msg || 'Unable to delete draft.');
      }

      /*
       * If the deleted draft is the report currently
       * loaded in the editor, reset the editor.
       */
      if (reportId === report._id) {
        const defaultType = config?.reportTypes?.[0] ?? 'MEDICAL';

        setReportId(null);

        setStatus(null);

        setAttachments([]);

        setDeletingAttachmentId(null);

        setForm(
          createEmptyForm(
            timezone,

            defaultType
          )
        );
      }

      setSuccess('Incident report draft deleted.');

      await loadHistory();
    } catch (error: any) {
      setError(
        getErrorMessage(
          error,

          'Unable to delete incident report draft.'
        )
      );
    } finally {
      setDeletingReportId(null);
    }
  };

  /* =====================================================
       ATTACHMENTS
       ===================================================== */

  const uploadAttachments = async (selectedFiles: File[]) => {
    if (readOnly || !validSite || selectedFiles.length === 0) {
      return;
    }

    if (attachments.length + selectedFiles.length > MAX_ATTACHMENT_COUNT) {
      setError(
        `A report can contain a maximum of ${MAX_ATTACHMENT_COUNT} attachments.`
      );

      return;
    }

    const invalidType = selectedFiles.find(
      (file) => !ALLOWED_ATTACHMENT_MIME_TYPES.has(file.type)
    );

    if (invalidType) {
      setError(
        `"${invalidType.name}" is not a supported file type. Only JPG, PNG and PDF files are allowed.`
      );

      return;
    }

    const oversizedFile = selectedFiles.find(
      (file) => file.size > MAX_ATTACHMENT_SIZE_BYTES
    );

    if (oversizedFile) {
      setError(`"${oversizedFile.name}" exceeds the 10 MB file-size limit.`);

      return;
    }

    setUploadingAttachments(true);

    setError('');

    setSuccess('');

    try {
      let id = reportId;

      /*
       * Attachments need a real report ID for their
       * private S3 storage path.
       *
       * If this is a brand-new unsaved report, create
       * the draft first and then continue the upload.
       */
      if (!id) {
        const fields = buildReportFields(
          form,

          timezone
        );

        const createResponse = await createIncidentReport({
          siteId,

          ...fields,
        });

        if (!createResponse.success || !createResponse.data) {
          throw new Error(
            createResponse.msg ||
              'Unable to create a draft before uploading evidence.'
          );
        }

        id = createResponse.data._id;

        setReportId(id);

        setStatus(createResponse.data.status);

        setForm(
          reportToForm(
            createResponse.data,

            timezone
          )
        );

        setAttachments(createResponse.data.attachments ?? []);
      }

      const response = await uploadIncidentReportAttachments(
        id,

        siteId,

        selectedFiles
      );

      if (!response.success || !response.data) {
        throw new Error(
          response.msg || 'Unable to upload incident report attachments.'
        );
      }

      setStatus(response.data.status);

      setAttachments(response.data.attachments ?? []);

      setSuccess(
        selectedFiles.length === 1
          ? 'Attachment uploaded successfully.'
          : `${selectedFiles.length} attachments uploaded successfully.`
      );

      if (showHistory) {
        await loadHistory();
      }
    } catch (error: any) {
      setError(
        getErrorMessage(
          error,

          'Unable to upload incident report attachments.'
        )
      );
    } finally {
      setUploadingAttachments(false);

      /*
       * Allow selecting the same file again after a
       * failed upload or after deleting it.
       */
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const deleteAttachment = async (attachment: IncidentAttachment) => {
    if (readOnly || !reportId || !attachment._id) {
      return;
    }

    const confirmed = window.confirm(
      `Delete attachment "${attachment.fileName || 'Untitled attachment'}"? This cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    setDeletingAttachmentId(attachment._id);

    setError('');

    setSuccess('');

    try {
      const response = await deleteIncidentReportAttachment(
        reportId,

        siteId,

        attachment._id
      );

      if (!response.success || !response.data) {
        throw new Error(response.msg || 'Unable to delete attachment.');
      }

      setAttachments(response.data.attachments ?? []);

      setSuccess('Attachment deleted.');
    } catch (error: any) {
      setError(
        getErrorMessage(
          error,

          'Unable to delete attachment.'
        )
      );
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  /* =====================================================
       TYPE-SPECIFIC FORM
       ===================================================== */

  const renderTypeFields = () => {
    if (!config) {
      return null;
    }

    switch (form.reportType) {
      case 'MEDICAL':
        return (
          <>
            <Field
              label="Nature of Medical Incident"
              className={styles.fullWidth}
            >
              <MultiSelect
                options={config.medical.incidentNatures}
                values={form.medical.incidentNatures}
                disabled={readOnly}
                onChange={(values) =>
                  setSection(
                    'medical',

                    'incidentNatures',

                    values
                  )
                }
              />
            </Field>

            {form.medical.incidentNatures.includes('OTHER') && (
              <Field
                label="Other Medical Incident"
                className={styles.fullWidth}
              >
                <input
                  value={form.medical.incidentNatureOther}
                  readOnly={readOnly}
                  onChange={(event) =>
                    setSection(
                      'medical',

                      'incidentNatureOther',

                      event.target.value
                    )
                  }
                />
              </Field>
            )}

            <Field label="Care Actions" className={styles.fullWidth}>
              <MultiSelect
                options={config.medical.careActions}
                values={form.medical.careActions}
                disabled={readOnly}
                onChange={(values) =>
                  setSection(
                    'medical',

                    'careActions',

                    values
                  )
                }
              />
            </Field>

            {form.medical.careActions.includes('OTHER') && (
              <Field label="Other Care Action" className={styles.fullWidth}>
                <input
                  value={form.medical.careActionOther}
                  readOnly={readOnly}
                  onChange={(event) =>
                    setSection(
                      'medical',

                      'careActionOther',

                      event.target.value
                    )
                  }
                />
              </Field>
            )}

            <Field label="Care Provided Narrative" className={styles.fullWidth}>
              <textarea
                rows={3}
                value={form.medical.careProvidedNarrative}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'medical',

                    'careProvidedNarrative',

                    event.target.value
                  )
                }
              />
            </Field>

            <Field label="EMS Called">
              <TriStateSelect
                value={form.medical.emsCalled}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'medical',

                    'emsCalled',

                    value
                  )
                }
              />
            </Field>

            {form.medical.emsCalled === true && (
              <Field label="EMS Agency">
                <input
                  value={form.medical.emsAgency}
                  readOnly={readOnly}
                  onChange={(event) =>
                    setSection(
                      'medical',

                      'emsAgency',

                      event.target.value
                    )
                  }
                />
              </Field>
            )}

            <Field label="Transported">
              <TriStateSelect
                value={form.medical.transported}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'medical',

                    'transported',

                    value
                  )
                }
              />
            </Field>

            {form.medical.transported === true && (
              <Field label="Destination">
                <input
                  value={form.medical.destination}
                  readOnly={readOnly}
                  onChange={(event) =>
                    setSection(
                      'medical',

                      'destination',

                      event.target.value
                    )
                  }
                />
              </Field>
            )}

            <Field label="Refused Care">
              <TriStateSelect
                value={form.medical.refusedCare}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'medical',

                    'refusedCare',

                    value
                  )
                }
              />
            </Field>

            <Field label="Additional Notes" className={styles.fullWidth}>
              <textarea
                rows={3}
                value={form.medical.additionalNotes}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'medical',

                    'additionalNotes',

                    event.target.value
                  )
                }
              />
            </Field>
          </>
        );

      case 'SECURITY_INCIDENT':
        return (
          <>
            <Field label="Incident Subtype">
              <ControlledSelect
                value={form.securityIncident.incidentSubtype}
                options={config.securityIncident.subtypes}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'securityIncident',

                    'incidentSubtype',

                    value
                  )
                }
              />
            </Field>

            {form.securityIncident.incidentSubtype === 'OTHER' && (
              <Field label="Other Subtype">
                <input
                  value={form.securityIncident.incidentSubtypeOther}
                  readOnly={readOnly}
                  onChange={(event) =>
                    setSection(
                      'securityIncident',

                      'incidentSubtypeOther',

                      event.target.value
                    )
                  }
                />
              </Field>
            )}

            <Field label="Law Enforcement Contacted">
              <TriStateSelect
                value={form.securityIncident.lawEnforcementContacted}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'securityIncident',

                    'lawEnforcementContacted',

                    value
                  )
                }
              />
            </Field>

            {form.securityIncident.lawEnforcementContacted === true && (
              <>
                <Field label="Agency">
                  <input
                    value={form.securityIncident.agency}
                    readOnly={readOnly}
                    onChange={(event) =>
                      setSection(
                        'securityIncident',

                        'agency',

                        event.target.value
                      )
                    }
                  />
                </Field>

                <Field label="Officer Name">
                  <input
                    value={form.securityIncident.officerName}
                    readOnly={readOnly}
                    onChange={(event) =>
                      setSection(
                        'securityIncident',

                        'officerName',

                        event.target.value
                      )
                    }
                  />
                </Field>

                <Field label="Case Number">
                  <input
                    value={form.securityIncident.caseNumber}
                    readOnly={readOnly}
                    onChange={(event) =>
                      setSection(
                        'securityIncident',

                        'caseNumber',

                        event.target.value
                      )
                    }
                  />
                </Field>
              </>
            )}

            <Field label="Additional Notes" className={styles.fullWidth}>
              <textarea
                rows={3}
                value={form.securityIncident.additionalNotes}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'securityIncident',

                    'additionalNotes',

                    event.target.value
                  )
                }
              />
            </Field>
          </>
        );

      case 'SUSPICIOUS_PERSON':
        return (
          <>
            <Field label="Sex / Presentation">
              <ControlledSelect
                value={form.suspiciousPerson.sexPresentation}
                options={config.suspiciousPerson.sexPresentations}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'suspiciousPerson',

                    'sexPresentation',

                    value
                  )
                }
              />
            </Field>

            <Field label="Approximate Age">
              <ControlledSelect
                value={form.suspiciousPerson.approximateAgeRange}
                options={config.suspiciousPerson.ageRanges}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'suspiciousPerson',

                    'approximateAgeRange',

                    value
                  )
                }
              />
            </Field>

            <Field label="Estimated Height">
              <div className={styles.heightRow}>
                <select
                  value={form.suspiciousPerson.heightFeet}
                  disabled={readOnly}
                  onChange={(event) =>
                    setSection(
                      'suspiciousPerson',

                      'heightFeet',

                      event.target.value
                    )
                  }
                >
                  <option value="">Feet</option>

                  {[2, 3, 4, 5, 6, 7, 8].map((feet) => (
                    <option key={feet} value={feet}>
                      {feet} ft
                    </option>
                  ))}
                </select>

                <select
                  value={form.suspiciousPerson.heightInches}
                  disabled={readOnly || !form.suspiciousPerson.heightFeet}
                  onChange={(event) =>
                    setSection(
                      'suspiciousPerson',

                      'heightInches',

                      event.target.value
                    )
                  }
                >
                  <option value="">0 in</option>

                  {Array.from({
                    length: 11,
                  }).map((_, index) => (
                    <option key={index + 1} value={index + 1}>
                      {index + 1} in
                    </option>
                  ))}
                </select>
              </div>
            </Field>

            <Field label="Build">
              <ControlledSelect
                value={form.suspiciousPerson.build}
                options={config.suspiciousPerson.builds}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'suspiciousPerson',

                    'build',

                    value
                  )
                }
              />
            </Field>

            <Field label="Hair Color">
              <ControlledSelect
                value={form.suspiciousPerson.hairColor}
                options={config.suspiciousPerson.hairColors}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'suspiciousPerson',

                    'hairColor',

                    value
                  )
                }
              />
            </Field>

            <Field label="Hair Length">
              <ControlledSelect
                value={form.suspiciousPerson.hairLength}
                options={config.suspiciousPerson.hairLengths}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'suspiciousPerson',

                    'hairLength',

                    value
                  )
                }
              />
            </Field>

            <Field label="Facial Hair">
              <ControlledSelect
                value={form.suspiciousPerson.facialHair}
                options={config.suspiciousPerson.facialHairTypes}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'suspiciousPerson',

                    'facialHair',

                    value
                  )
                }
              />
            </Field>

            <Field label="Direction">
              <ControlledSelect
                value={form.suspiciousPerson.directionOfTravel}
                options={config.directions}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'suspiciousPerson',

                    'directionOfTravel',

                    value
                  )
                }
              />
            </Field>

            <Field label="Clothing" className={styles.fullWidth}>
              <textarea
                rows={2}
                value={form.suspiciousPerson.clothing}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'suspiciousPerson',

                    'clothing',

                    event.target.value
                  )
                }
              />
            </Field>

            <Field label="Identifying Features" className={styles.fullWidth}>
              <textarea
                rows={2}
                value={form.suspiciousPerson.identifyingFeatures}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'suspiciousPerson',

                    'identifyingFeatures',

                    event.target.value
                  )
                }
              />
            </Field>

            <Field label="Observed Behavior" className={styles.fullWidth}>
              <textarea
                rows={3}
                value={form.suspiciousPerson.behavior}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'suspiciousPerson',

                    'behavior',

                    event.target.value
                  )
                }
              />
            </Field>

            <Field label="Associated Vehicle" className={styles.fullWidth}>
              <input
                value={form.suspiciousPerson.associatedVehicle}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'suspiciousPerson',

                    'associatedVehicle',

                    event.target.value
                  )
                }
              />
            </Field>
          </>
        );

      case 'SUSPICIOUS_VEHICLE':
        return (
          <>
            <Field label="License Plate">
              <input
                value={form.suspiciousVehicle.licensePlate}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'suspiciousVehicle',

                    'licensePlate',

                    event.target.value.toUpperCase()
                  )
                }
              />
            </Field>

            <Field label="State">
              <ControlledSelect
                value={form.suspiciousVehicle.state}
                options={config.usStates}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'suspiciousVehicle',

                    'state',

                    value
                  )
                }
              />
            </Field>

            <Field label="Make">
              <input
                value={form.suspiciousVehicle.make}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'suspiciousVehicle',

                    'make',

                    event.target.value
                  )
                }
              />
            </Field>

            <Field label="Model">
              <input
                value={form.suspiciousVehicle.model}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'suspiciousVehicle',

                    'model',

                    event.target.value
                  )
                }
              />
            </Field>

            <Field label="Color">
              <ControlledSelect
                value={form.suspiciousVehicle.color}
                options={config.suspiciousVehicle.colors}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'suspiciousVehicle',

                    'color',

                    value
                  )
                }
              />
            </Field>

            <Field label="Body Type">
              <ControlledSelect
                value={form.suspiciousVehicle.bodyType}
                options={config.suspiciousVehicle.bodyTypes}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'suspiciousVehicle',

                    'bodyType',

                    value
                  )
                }
              />
            </Field>

            <Field label="Occupants">
              <input
                type="number"
                min={0}
                max={99}
                step={1}
                value={form.suspiciousVehicle.occupantCount}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'suspiciousVehicle',

                    'occupantCount',

                    event.target.value
                  )
                }
              />
            </Field>

            <Field label="Direction">
              <ControlledSelect
                value={form.suspiciousVehicle.directionOfTravel}
                options={config.directions}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'suspiciousVehicle',

                    'directionOfTravel',

                    value
                  )
                }
              />
            </Field>

            <Field label="Distinguishing Marks" className={styles.fullWidth}>
              <textarea
                rows={3}
                value={form.suspiciousVehicle.distinguishingMarks}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'suspiciousVehicle',

                    'distinguishingMarks',

                    event.target.value
                  )
                }
              />
            </Field>
          </>
        );

      case 'FIRE_SMOKE':
        return (
          <>
            <Field label="Source Location">
              <input
                value={form.fireSmoke.sourceLocation}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'fireSmoke',

                    'sourceLocation',

                    event.target.value
                  )
                }
              />
            </Field>

            <Field label="Suspected Source">
              <ControlledSelect
                value={form.fireSmoke.suspectedSource}
                options={config.fireSmoke.sources}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'fireSmoke',

                    'suspectedSource',

                    value
                  )
                }
              />
            </Field>

            {form.fireSmoke.suspectedSource === 'OTHER' && (
              <Field label="Other Source">
                <input
                  value={form.fireSmoke.suspectedSourceOther}
                  readOnly={readOnly}
                  onChange={(event) =>
                    setSection(
                      'fireSmoke',

                      'suspectedSourceOther',

                      event.target.value
                    )
                  }
                />
              </Field>
            )}

            {[
              ['alarmActivated', 'Alarm Activated'],
              ['extinguisherUsed', 'Extinguisher Used'],
              ['evacuationOccurred', 'Evacuation Occurred'],
              ['fireDepartmentCalled', 'Fire Department Called'],
            ].map(([key, label]) => (
              <Field key={key} label={label}>
                <TriStateSelect
                  value={(form.fireSmoke as any)[key]}
                  disabled={readOnly}
                  onChange={(value) =>
                    setSection(
                      'fireSmoke',

                      key,

                      value
                    )
                  }
                />
              </Field>
            ))}

            {form.fireSmoke.fireDepartmentCalled === true && (
              <Field label="Fire Department / Agency">
                <input
                  value={form.fireSmoke.fireDepartmentAgency}
                  readOnly={readOnly}
                  onChange={(event) =>
                    setSection(
                      'fireSmoke',

                      'fireDepartmentAgency',

                      event.target.value
                    )
                  }
                />
              </Field>
            )}

            <Field label="Additional Notes" className={styles.fullWidth}>
              <textarea
                rows={3}
                value={form.fireSmoke.additionalNotes}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'fireSmoke',

                    'additionalNotes',

                    event.target.value
                  )
                }
              />
            </Field>
          </>
        );

      case 'SAFETY_HAZARD':
        return (
          <>
            <Field label="Hazard Types" className={styles.fullWidth}>
              <MultiSelect
                options={config.safetyHazard.hazardTypes}
                values={form.safetyHazard.hazardTypes}
                disabled={readOnly}
                onChange={(values) =>
                  setSection(
                    'safetyHazard',

                    'hazardTypes',

                    values
                  )
                }
              />
            </Field>

            {form.safetyHazard.hazardTypes.includes('OTHER') && (
              <Field label="Other Hazard" className={styles.fullWidth}>
                <input
                  value={form.safetyHazard.hazardTypeOther}
                  readOnly={readOnly}
                  onChange={(event) =>
                    setSection(
                      'safetyHazard',

                      'hazardTypeOther',

                      event.target.value
                    )
                  }
                />
              </Field>
            )}

            <Field label="Area Secured">
              <TriStateSelect
                value={form.safetyHazard.areaSecured}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'safetyHazard',

                    'areaSecured',

                    value
                  )
                }
              />
            </Field>

            <Field label="Corrective Action" className={styles.fullWidth}>
              <textarea
                rows={3}
                value={form.safetyHazard.correctiveAction}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'safetyHazard',

                    'correctiveAction',

                    event.target.value
                  )
                }
              />
            </Field>
          </>
        );

      case 'ACCESS_CONTROL':
        return (
          <>
            <Field label="Access Point">
              <ControlledSelect
                value={form.accessControl.accessPointType}
                options={config.accessControl.pointTypes}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'accessControl',

                    'accessPointType',

                    value
                  )
                }
              />
            </Field>

            {form.accessControl.accessPointType === 'OTHER' && (
              <Field label="Other Access Point">
                <input
                  value={form.accessControl.accessPointOther}
                  readOnly={readOnly}
                  onChange={(event) =>
                    setSection(
                      'accessControl',

                      'accessPointOther',

                      event.target.value
                    )
                  }
                />
              </Field>
            )}

            <Field label="Access Method">
              <ControlledSelect
                value={form.accessControl.accessMethod}
                options={config.accessControl.methods}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'accessControl',

                    'accessMethod',

                    value
                  )
                }
              />
            </Field>

            {form.accessControl.accessMethod === 'OTHER' && (
              <Field label="Other Access Method">
                <input
                  value={form.accessControl.accessMethodOther}
                  readOnly={readOnly}
                  onChange={(event) =>
                    setSection(
                      'accessControl',

                      'accessMethodOther',

                      event.target.value
                    )
                  }
                />
              </Field>
            )}

            <Field label="Authorized">
              <TriStateSelect
                value={form.accessControl.authorized}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'accessControl',

                    'authorized',

                    value
                  )
                }
              />
            </Field>

            <Field label="Attempted Entry">
              <TriStateSelect
                value={form.accessControl.attemptedEntry}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'accessControl',

                    'attemptedEntry',

                    value
                  )
                }
              />
            </Field>

            <Field label="Credential Description" className={styles.fullWidth}>
              <input
                value={form.accessControl.credentialDescription}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'accessControl',

                    'credentialDescription',

                    event.target.value
                  )
                }
              />
            </Field>
          </>
        );

      case 'PROPERTY_DAMAGE':
        return (
          <>
            <Field label="Property Category">
              <ControlledSelect
                value={form.propertyDamage.propertyCategory}
                options={config.propertyDamage.categories}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'propertyDamage',

                    'propertyCategory',

                    value
                  )
                }
              />
            </Field>

            {form.propertyDamage.propertyCategory === 'OTHER' && (
              <Field label="Other Property Category">
                <input
                  value={form.propertyDamage.propertyCategoryOther}
                  readOnly={readOnly}
                  onChange={(event) =>
                    setSection(
                      'propertyDamage',

                      'propertyCategoryOther',

                      event.target.value
                    )
                  }
                />
              </Field>
            )}

            <Field label="Suspected Cause">
              <ControlledSelect
                value={form.propertyDamage.suspectedCause}
                options={config.propertyDamage.causes}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'propertyDamage',

                    'suspectedCause',

                    value
                  )
                }
              />
            </Field>

            <Field label="Estimated Damage ($)">
              <input
                type="number"
                min={0}
                value={form.propertyDamage.estimatedDamageAmount}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'propertyDamage',

                    'estimatedDamageAmount',

                    event.target.value
                  )
                }
              />
            </Field>

            <Field label="Property Description" className={styles.fullWidth}>
              <textarea
                rows={3}
                value={form.propertyDamage.propertyDescription}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'propertyDamage',

                    'propertyDescription',

                    event.target.value
                  )
                }
              />
            </Field>
          </>
        );

      case 'THEFT_LOSS':
        return (
          <>
            <Field label="Loss Type">
              <ControlledSelect
                value={form.theftLoss.lossType}
                options={config.theftLoss.lossTypes}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'theftLoss',

                    'lossType',

                    value
                  )
                }
              />
            </Field>

            <Field label="Property Category">
              <ControlledSelect
                value={form.theftLoss.propertyCategory}
                options={config.theftLoss.propertyCategories}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'theftLoss',

                    'propertyCategory',

                    value
                  )
                }
              />
            </Field>

            {form.theftLoss.propertyCategory === 'OTHER' && (
              <Field
                label="Other Property Category"
                className={styles.fullWidth}
              >
                <input
                  value={form.theftLoss.propertyCategoryOther}
                  readOnly={readOnly}
                  onChange={(event) =>
                    setSection(
                      'theftLoss',

                      'propertyCategoryOther',

                      event.target.value
                    )
                  }
                />
              </Field>
            )}

            <Field label="Estimated Value ($)">
              <input
                type="number"
                min={0}
                value={form.theftLoss.estimatedValue}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'theftLoss',

                    'estimatedValue',

                    event.target.value
                  )
                }
              />
            </Field>

            <Field label="Last Known Date & Time">
              <input
                type="datetime-local"
                value={form.theftLoss.lastKnownAt}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'theftLoss',

                    'lastKnownAt',

                    event.target.value
                  )
                }
              />
            </Field>

            <Field label="Item Description" className={styles.fullWidth}>
              <textarea
                rows={3}
                value={form.theftLoss.itemDescription}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'theftLoss',

                    'itemDescription',

                    event.target.value
                  )
                }
              />
            </Field>

            <Field label="Last Known Location" className={styles.fullWidth}>
              <input
                value={form.theftLoss.lastKnownLocation}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'theftLoss',

                    'lastKnownLocation',

                    event.target.value
                  )
                }
              />
            </Field>
          </>
        );

      case 'CLIENT_DISSATISFACTION':
        return (
          <>
            <Field label="Complaint Source">
              <ControlledSelect
                value={form.clientDissatisfaction.complaintSource}
                options={config.clientFeedback.sources}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'clientDissatisfaction',

                    'complaintSource',

                    value
                  )
                }
              />
            </Field>

            <Field label="Complaint Category">
              <ControlledSelect
                value={form.clientDissatisfaction.complaintCategory}
                options={config.clientFeedback.complaintCategories}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'clientDissatisfaction',

                    'complaintCategory',

                    value
                  )
                }
              />
            </Field>

            <Field
              label="Employee / Team Involved"
              className={styles.fullWidth}
            >
              <input
                value={form.clientDissatisfaction.employeeOrTeamInvolved}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'clientDissatisfaction',

                    'employeeOrTeamInvolved',

                    event.target.value
                  )
                }
              />
            </Field>

            <Field label="Complaint Summary" className={styles.fullWidth}>
              <textarea
                rows={3}
                value={form.clientDissatisfaction.complaintSummary}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'clientDissatisfaction',

                    'complaintSummary',

                    event.target.value
                  )
                }
              />
            </Field>

            <Field label="Immediate Resolution" className={styles.fullWidth}>
              <textarea
                rows={3}
                value={form.clientDissatisfaction.immediateResolution}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'clientDissatisfaction',

                    'immediateResolution',

                    event.target.value
                  )
                }
              />
            </Field>

            <Field label="Follow-Up Required">
              <TriStateSelect
                value={form.clientDissatisfaction.followUpRequired}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'clientDissatisfaction',

                    'followUpRequired',

                    value
                  )
                }
              />
            </Field>
          </>
        );

      case 'CLIENT_PRAISE':
        return (
          <>
            <Field label="Praise Source">
              <ControlledSelect
                value={form.clientPraise.praiseSource}
                options={config.clientFeedback.sources}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'clientPraise',

                    'praiseSource',

                    value
                  )
                }
              />
            </Field>

            <Field label="Praise Category">
              <ControlledSelect
                value={form.clientPraise.praiseCategory}
                options={config.clientFeedback.praiseCategories}
                disabled={readOnly}
                onChange={(value) =>
                  setSection(
                    'clientPraise',

                    'praiseCategory',

                    value
                  )
                }
              />
            </Field>

            <Field label="Employee / Team Praised" className={styles.fullWidth}>
              <input
                value={form.clientPraise.praisedEmployeeNameSnapshot}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'clientPraise',

                    'praisedEmployeeNameSnapshot',

                    event.target.value
                  )
                }
              />
            </Field>

            <Field label="Praise Summary" className={styles.fullWidth}>
              <textarea
                rows={4}
                value={form.clientPraise.praiseSummary}
                readOnly={readOnly}
                onChange={(event) =>
                  setSection(
                    'clientPraise',

                    'praiseSummary',

                    event.target.value
                  )
                }
              />
            </Field>
          </>
        );

      default:
        return (
          <div className={styles.otherNotice}>
            Use the Narrative / Summary and Actions Taken fields below to
            document this report.
          </div>
        );
    }
  };

  /* =====================================================
       EMPTY / LOADING STATES
       ===================================================== */

  if (!validSite) {
    return (
      <div className={styles.page}>
        <div className={styles.noSiteState}>
          <BsShieldFillExclamation />

          <h1>Select a site</h1>

          <p>
            Select a client site before creating or reviewing incident reports.
          </p>
        </div>
      </div>
    );
  }

  if (configLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.configState}>
          <BsShieldFillExclamation />

          <h1>Loading Incident Report</h1>

          <p>Loading standardized report values…</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className={styles.page}>
        <div className={styles.configState}>
          <BsExclamationTriangle />

          <h1>Report taxonomy unavailable</h1>

          <p>{configError}</p>

          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => void loadConfig()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  /* =====================================================
       UI
       ===================================================== */

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerTitleGroup}>
          <div className={styles.headerIcon}>
            <BsShieldFillExclamation />
          </div>

          <div className={styles.titleCopy}>
            <div className={styles.headerTitleRow}>
              <h1 className={styles.headerTitle}>Incident Report</h1>

              <span className={styles.sitePill}>{siteName}</span>

              {status && (
                <span
                  className={[
                    styles.statusPill,

                    status === 'SUBMITTED'
                      ? styles.statusSubmitted
                      : styles.statusDraft,
                  ].join(' ')}
                >
                  {status}
                </span>
              )}
            </div>

            <p className={styles.headerSubtitle}>
              Structured incident reporting for security, safety, medical and
              client-service events.
            </p>
          </div>
        </div>

        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => {
              setForm(
                createEmptyForm(
                  timezone,

                  config.reportTypes[0]
                )
              );

              setReportId(null);

              setStatus(null);

              setAttachments([]);

              setUploadingAttachments(false);

              setDeletingAttachmentId(null);
            }}
          >
            + New Report
          </button>

          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => setShowHistory((previous) => !previous)}
          >
            <BsClockHistory />

            {showHistory ? 'Hide History' : 'View History'}
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          <BsExclamationTriangle />

          {error}
        </div>
      )}

      {success && <div className={styles.successBanner}>{success}</div>}

      {readOnly && (
        <div className={styles.submittedNotice}>
          <BsShieldFillExclamation />

          <div>
            <strong>Submitted Report</strong>

            <span>Submitted reports are read-only.</span>
          </div>
        </div>
      )}

      <section className={styles.workspace}>
        <div className={styles.typePanel}>
          <div className={styles.panelHeading}>
            <span className={styles.stepNumber}>1</span>

            <div>
              <h2>Report Type</h2>

              <p>Select the category that best matches the incident.</p>
            </div>
          </div>

          <div className={styles.reportTypeGrid}>
            {config.reportTypes.map((type) => (
              <button
                key={type}
                type="button"
                disabled={readOnly}
                className={[
                  styles.reportTypeCard,

                  form.reportType === type ? styles.reportTypeCardActive : '',
                ].join(' ')}
                onClick={() =>
                  setForm((previous) => ({
                    ...previous,

                    reportType: type,
                  }))
                }
              >
                <span className={styles.reportTypeIcon}>
                  {REPORT_TYPE_META[type]?.icon}
                </span>

                <span className={styles.reportTypeLabel}>
                  {REPORT_TYPE_META[type]?.label ?? enumLabel(type)}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className={styles.detailsPanel}>
          <div className={styles.panelHeading}>
            <span className={styles.stepNumber}>2</span>

            <div>
              <h2>Report Details</h2>

              <p>{REPORT_TYPE_META[form.reportType]?.label}</p>
            </div>
          </div>

          <div className={styles.formGrid}>
            <Field label="Report Title" required className={styles.spanTwo}>
              <input
                value={form.title}
                readOnly={readOnly}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,

                    title: event.target.value,
                  }))
                }
              />
            </Field>

            <Field label="Site">
              <input value={siteName} readOnly />
            </Field>

            <Field label="Specific Location" required>
              <div className={styles.locationInput}>
                <BsGeoAltFill />

                <input
                  value={form.locationLabel}
                  readOnly={readOnly}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,

                      locationLabel: event.target.value,
                    }))
                  }
                />
              </div>
            </Field>

            <Field label="Date & Time" required>
              <input
                type="datetime-local"
                value={form.occurredAt}
                readOnly={readOnly}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,

                    occurredAt: event.target.value,
                  }))
                }
              />
            </Field>

            <Field label="Severity" required>
              <ControlledSelect
                value={form.severity}
                options={config.severities}
                disabled={readOnly}
                onChange={(value) =>
                  setForm((previous) => ({
                    ...previous,

                    severity: value as any,
                  }))
                }
              />
            </Field>
          </div>

          <div className={styles.typeSpecificSection}>
            <div className={styles.typeSpecificHeader}>
              <strong>
                {REPORT_TYPE_META[form.reportType]?.label} Details
              </strong>

              <span>Standardized fields are used wherever practical.</span>
            </div>

            <div className={styles.typeSpecificGrid}>{renderTypeFields()}</div>
          </div>

          <div className={styles.personsSection}>
            <div className={styles.sectionTop}>
              <div>
                <strong>Persons Involved</strong>

                <span>Roles are standardized for analytics.</span>
              </div>

              {!readOnly && (
                <button
                  type="button"
                  className={styles.addPersonButton}
                  onClick={addPerson}
                >
                  + Add Person
                </button>
              )}
            </div>

            {form.personsInvolved.map((person, index) => (
              <div key={index} className={styles.personCard}>
                <div className={styles.personFields}>
                  <Field label="Name">
                    <input
                      value={person.name}
                      readOnly={readOnly}
                      onChange={(event) =>
                        updatePerson(
                          index,

                          'name',

                          event.target.value
                        )
                      }
                    />
                  </Field>

                  <Field label="Role">
                    <ControlledSelect
                      value={person.role}
                      options={config.personRoles}
                      disabled={readOnly}
                      onChange={(value) =>
                        updatePerson(
                          index,

                          'role',

                          value
                        )
                      }
                    />
                  </Field>

                  {person.role === 'OTHER' && (
                    <Field label="Other Role">
                      <input
                        value={person.roleOther}
                        readOnly={readOnly}
                        onChange={(event) =>
                          updatePerson(
                            index,

                            'roleOther',

                            event.target.value
                          )
                        }
                      />
                    </Field>
                  )}

                  <Field label="Notes">
                    <input
                      value={person.notes}
                      readOnly={readOnly}
                      onChange={(event) =>
                        updatePerson(
                          index,

                          'notes',

                          event.target.value
                        )
                      }
                    />
                  </Field>
                </div>

                {!readOnly && (
                  <button
                    type="button"
                    className={styles.removePersonButton}
                    onClick={() =>
                      setForm((previous) => ({
                        ...previous,

                        personsInvolved: previous.personsInvolved.filter(
                          (_, personIndex) => personIndex !== index
                        ),
                      }))
                    }
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className={styles.narrativeSection}>
            <Field label="Narrative / Summary" required>
              <textarea
                rows={6}
                maxLength={10000}
                value={form.narrative}
                readOnly={readOnly}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,

                    narrative: event.target.value,
                  }))
                }
              />
            </Field>

            <Field label="Actions Taken" required>
              <textarea
                rows={5}
                maxLength={10000}
                value={form.actionsTaken}
                readOnly={readOnly}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,

                    actionsTaken: event.target.value,
                  }))
                }
              />
            </Field>
          </div>

          <div className={styles.attachmentSection}>
            <div className={styles.attachmentHeading}>
              <div>
                <strong>Attachments / Evidence</strong>

                <span>
                  JPG, PNG or PDF • 10 MB maximum per file • 10 attachments
                  maximum
                </span>
              </div>

              <span className={styles.attachmentCount}>
                {attachments.length}/{MAX_ATTACHMENT_COUNT}
              </span>
            </div>

            {!readOnly && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  className={styles.attachmentFileInput}
                  accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                  multiple
                  disabled={
                    uploadingAttachments ||
                    saving ||
                    attachments.length >= MAX_ATTACHMENT_COUNT
                  }
                  onChange={(event) => {
                    const files = Array.from(event.target.files ?? []);

                    void uploadAttachments(files);
                  }}
                />

                <div className={styles.attachmentUploadArea}>
                  <div>
                    <strong>Add Evidence</strong>

                    <span>
                      {reportId
                        ? 'Files are stored privately with this draft.'
                        : 'Selecting a file will automatically save this report as a draft first.'}
                    </span>
                  </div>

                  <button
                    type="button"
                    className={styles.attachmentUploadButton}
                    disabled={
                      uploadingAttachments ||
                      saving ||
                      attachments.length >= MAX_ATTACHMENT_COUNT
                    }
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploadingAttachments
                      ? 'Uploading…'
                      : attachments.length >= MAX_ATTACHMENT_COUNT
                        ? 'Attachment Limit Reached'
                        : '+ Add Files'}
                  </button>
                </div>
              </>
            )}

            {attachments.length === 0 ? (
              <div className={styles.attachmentPlaceholder}>
                <strong>No attachments added</strong>

                <small>Photos and PDF evidence will appear here.</small>
              </div>
            ) : (
              <div className={styles.attachmentGrid}>
                {attachments.map((attachment) => {
                  const isPhoto =
                    attachment.type === 'PHOTO' &&
                    attachment.mimeType?.startsWith('image/');

                  const attachmentKey = attachment._id || attachment.storageKey;

                  return (
                    <div key={attachmentKey} className={styles.attachmentCard}>
                      {isPhoto && attachment.url ? (
                        <a
                          href={attachment.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className={styles.attachmentPreview}
                          title="Open image"
                        >
                          <img
                            src={attachment.url}
                            alt={
                              attachment.fileName ||
                              'Incident report attachment'
                            }
                          />
                        </a>
                      ) : (
                        <div className={styles.attachmentDocumentPreview}>
                          <span>
                            {attachment.mimeType === 'application/pdf'
                              ? 'PDF'
                              : 'FILE'}
                          </span>
                        </div>
                      )}

                      <div className={styles.attachmentMeta}>
                        <strong title={attachment.fileName || 'Attachment'}>
                          {attachment.fileName || 'Attachment'}
                        </strong>

                        <span>
                          {attachment.type}
                          {attachment.size !== undefined
                            ? ` • ${formatFileSize(attachment.size)}`
                            : ''}
                        </span>
                      </div>

                      <div className={styles.attachmentActions}>
                        {attachment.url && (
                          <a
                            href={attachment.url}
                            target="_blank"
                            rel="noreferrer noopener"
                            className={styles.attachmentViewButton}
                          >
                            View
                          </a>
                        )}

                        {!readOnly && attachment._id && (
                          <button
                            type="button"
                            className={styles.attachmentDeleteButton}
                            disabled={deletingAttachmentId === attachment._id}
                            onClick={() => void deleteAttachment(attachment)}
                          >
                            {deletingAttachmentId === attachment._id
                              ? 'Deleting…'
                              : 'Delete'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className={styles.notificationSection}>
            <label className={styles.notificationToggle}>
              <div>
                <strong>Notify Supervisor</strong>

                <span>
                  Store the supervisor notification preference with this report.
                </span>
              </div>

              <input
                type="checkbox"
                checked={form.notifySupervisor}
                disabled={readOnly}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,

                    notifySupervisor: event.target.checked,
                  }))
                }
              />
            </label>
          </div>

          <div className={styles.actionBar}>
            {!readOnly && (
              <>
                <button
                  type="button"
                  className={styles.saveButton}
                  disabled={saving}
                  onClick={() => void saveDraft()}
                >
                  <BsBookmark />

                  {reportId ? 'Update Draft' : 'Save Draft'}
                </button>

                <button
                  type="button"
                  className={styles.submitButton}
                  disabled={saving}
                  onClick={() => void submit()}
                >
                  <BsSendFill />
                  Submit Report
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      {showHistory && (
        <section className={styles.historyCard}>
          <div className={styles.historyHeader}>
            <div>
              <h2>Incident Report History</h2>

              <p>{siteName}</p>
            </div>

            <div className={styles.historyCount}>
              {reports.length} {reports.length === 1 ? 'report' : 'reports'}
            </div>
          </div>

          <div className={styles.historyFilters}>
            <div className={styles.historyPresetButtons}>
              {[
                ['7', '7 Days'],
                ['30', '30 Days'],
                ['90', '90 Days'],
                ['180', '180 Days'],
                ['365', '365 Days'],
                ['CUSTOM', 'Date Range'],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={[
                    styles.historyFilterButton,

                    historyRange === value
                      ? styles.historyFilterButtonActive
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => {
                    const nextRange = value as HistoryRangePreset;

                    if (nextRange === 'CUSTOM' && !customFrom && !customTo) {
                      setCustomFrom(
                        moment()
                          .tz(timezone)
                          .subtract(29, 'days')
                          .format('YYYY-MM-DD')
                      );

                      setCustomTo(moment().tz(timezone).format('YYYY-MM-DD'));
                    }

                    setHistoryRange(nextRange);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {historyRange === 'CUSTOM' && (
              <div className={styles.customDateRange}>
                <label>
                  <span>From</span>

                  <input
                    type="date"
                    value={customFrom}
                    max={customTo || undefined}
                    onChange={(event) => setCustomFrom(event.target.value)}
                  />
                </label>

                <label>
                  <span>To</span>

                  <input
                    type="date"
                    value={customTo}
                    min={customFrom || undefined}
                    onChange={(event) => setCustomTo(event.target.value)}
                  />
                </label>
              </div>
            )}
          </div>

          {historyLoading ? (
            <div className={styles.historyEmpty}>Loading…</div>
          ) : (
            <div className={styles.historyList}>
              {reports.map((report) => (
                <div
                  key={report._id}
                  className={[
                    styles.historyRow,

                    reportId === report._id ? styles.historyRowActive : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <button
                    type="button"
                    className={styles.historyRowMain}
                    onClick={() => void openReport(report._id)}
                  >
                    <div className={styles.historyPrimary}>
                      <strong>{report.title || 'Untitled Report'}</strong>

                      <span>
                        {report.occurredAt
                          ? moment(report.occurredAt)
                              .tz(timezone)
                              .format('MMM D, YYYY • h:mm A')
                          : ''}
                      </span>
                    </div>

                    <div className={styles.historyType}>
                      {REPORT_TYPE_META[report.reportType]?.label}
                    </div>

                    <div
                      className={[
                        styles.historyStatus,

                        report.status === 'SUBMITTED'
                          ? styles.statusSubmitted
                          : styles.statusDraft,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {report.status}
                    </div>
                  </button>

                  {report.status === 'DRAFT' && (
                    <button
                      type="button"
                      className={styles.deleteDraftButton}
                      disabled={deletingReportId === report._id}
                      onClick={() => void deleteDraft(report)}
                    >
                      {deletingReportId === report._id ? 'Deleting…' : 'Delete'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default IncidentReport;
